import React from 'react';
import _get from 'lodash/get';
import {
  FormTemplateQuestionFieldsKindEnum,
  TaskCompletion,
} from 'graphql-common';
import {
  Column,
  CellProps,
  CellContentProps,
} from '@lib/components/Table/types';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import getFullName from '@lib/utils/getFullName';
import {
  getQuestionColumn,
  getResponseCellNode,
  getValidityHintColumn,
  ResponseCellArgs,
} from '../../../utils/getTaskCompletionDetailsTableColumns';
import { CompareCompletionColumn, CompareCompletionRow } from './types';
import {
  DocumentIdentityCell,
  DocumentStatusCell,
} from './DocumentCompareCells';
import styles from '../TaskCompletionsCompareTable.module.scss';

export type CompareResponseCellArgs = ResponseCellArgs;

function getColumnHeader(completion: TaskCompletion | null | undefined) {
  const performer = completion?.performer;
  const date = completion?.createdAt
    ? getFormattedDate(completion.createdAt, dateTimeFormatEU)
    : undefined;

  return (
    <div className={styles.columnHeader}>
      <UserPreviewCell
        name={getFullName(performer)}
        email={date}
        avatar={performer?.avatarAttached?.urls?.small}
      />
    </div>
  );
}

export default function getCompareCompletionsTableColumns({
  columns,
  responseArgs,
}: {
  columns: CompareCompletionColumn[];
  responseArgs: CompareResponseCellArgs;
}): Column<CompareCompletionRow>[] {
  const { t, onDocumentClick, isViewLocked, isFullAdmin, onUpgradePlan } =
    responseArgs;

  const getWorstValidityHint = (item: CompareCompletionRow) =>
    item.worstValidityHint;
  const validityHintColumn = getValidityHintColumn(getWorstValidityHint);
  const questionColumn: Column<CompareCompletionRow> = {
    ...getQuestionColumn({ t, getValidityHint: getWorstValidityHint }),
    // Only document questions carry a value in the dedicated document column
    // right after this one — every other kind shows a "-" there, so this
    // cell widens into that empty space instead.
    getColSpan: ({ kind }) =>
      kind === FormTemplateQuestionFieldsKindEnum.Document ? 1 : 2,
  };

  // Document questions are compared differently from every other kind: the
  // document identity (preview/name/size) is the same across every
  // completion, so it's rendered once here instead of once per response
  // column — each response column below then only carries that document's
  // per-completion read status + reading time.
  const documentColumn: Column<CompareCompletionRow> = {
    id: 'document',
    Header: '',
    Cell: (props: CellProps<CompareCompletionRow>) => {
      const { item } = props;
      if (item.kind !== FormTemplateQuestionFieldsKindEnum.Document) {
        return <TextCell {...props} value="-" />;
      }
      return (
        <DocumentIdentityCell
          {...props}
          t={t}
          onDocumentClick={onDocumentClick}
          isViewLocked={isViewLocked}
          isFullAdmin={isFullAdmin}
          onUpgradePlan={onUpgradePlan}
        />
      );
    },
    minWidth: 300,
    maxWidth: 300,
  };

  const responseColumns: Column<CompareCompletionRow>[] = columns.map(
    (column, columnIndex) => ({
      id: `response-${column.id}`,
      Header: getColumnHeader(column.completion),
      isCustomHeader: true,
      accessorFn: ({ item }: CellContentProps<CompareCompletionRow>) =>
        _get(item.columnItems[columnIndex], ['answer', 'resp', 0, 'value']) ??
        '-',
      Cell: (props: CellProps<CompareCompletionRow>) => {
        const { item } = props;
        const columnItem = item.columnItems[columnIndex];
        if (!columnItem) return <TextCell {...props} value="-" />;
        if (item.kind === FormTemplateQuestionFieldsKindEnum.Document) {
          return (
            <DocumentStatusCell {...props} t={t} answer={columnItem.answer} />
          );
        }
        // The cell's own background must reflect the row's worst status
        // (across every compared completion), not just this column's own
        // answer — a success answer sitting next to a failure in the same
        // row still gets the red crop background.
        return getResponseCellNode(
          { ...props, item: columnItem },
          { ...responseArgs, validityHintOverride: item.worstValidityHint },
        );
      },
      minWidth: 300,
      maxWidth: 300,
    }),
  );

  return [
    validityHintColumn,
    questionColumn,
    documentColumn,
    ...responseColumns,
  ];
}
